import type { IconType } from "react-icons";
import {
	FiActivity,
	FiBook,
	FiBox,
	FiBriefcase,
	FiCloud,
	FiCornerUpRight,
	FiDollarSign,
	FiFileText,
	FiFolder,
	FiGitBranch,
	FiGlobe,
	FiGrid,
	FiInbox,
	FiLayers,
	FiLayout,
	FiList,
	FiKey,
	FiMail,
	FiMap,
	FiDatabase,
	FiPenTool,
	FiRefreshCw,
	FiRotateCcw,
	FiSearch,
	FiSettings,
	FiShield,
	FiTag,
	FiTrendingUp,
	FiUploadCloud,
	FiUsers,
	FiWifi
} from "react-icons/fi";

export interface TableColumn<T> {
	key: keyof T | string; // can be a property of T or a virtual key
	label: string;
	render?: (item: T, history?: any, refresh?: () => Promise<void>) => React.ReactNode;
}

// Build-time fallback. The runtime value resolved from `/api/public-config`
// lives on `window.__BCC_RUNTIME_CONFIG__.resourcesPublicBaseUrl` (set by
// `index.tsx` before React renders). `Helper/storage.tsx` reads the runtime
// value when present and falls back to this constant when not.
export const STORAGE_PUBLIC_BASE_URL = "https://pub-07bef1ece3d340c58889b1638c687dd3.r2.dev";
export const STORAGE_INTERNAL_BASE_URL = "https://pub-223a87f8049b4cb6b3b1ddc1af8b1592.r2.dev";

/** Base URL of the external public content API (BCC.PUBLIC.API), used by the
 * "API publique" page's domain panel + live tester. Overridable at build time
 * via REACT_APP_PUBLIC_API_URL; defaults to the deployed production host. */
export const PUBLIC_API_BASE_URL = (
	process.env.REACT_APP_PUBLIC_API_URL?.trim() ||
	"https://admin.bcc.cd"
).replace(/\/+$/, "");

declare global {
	interface Window {
		__BCC_RUNTIME_CONFIG__?: {
			resourcesPublicBaseUrl?: string | null;
			publicResourcesBaseUrl?: string | null;
		};
	}
}

/** Returns the runtime-resolved public storage base URL or falls back to the
 * build-time constant. Called by `Helper/storage.tsx` on every URL resolution
 * so the value follows changes (e.g. once the runtime fetch lands). */
export const getStoragePublicBaseUrl = (): string => {
	if (typeof window !== "undefined") {
		const runtime = window.__BCC_RUNTIME_CONFIG__?.resourcesPublicBaseUrl;
		if (typeof runtime === "string" && runtime.trim()) return runtime.trim();
	}
	return STORAGE_PUBLIC_BASE_URL;
};


export type NavItem = {
	type: "item";
	to: string;
	label: string;
	icon: IconType;
	/** Optional live badge to render after the label (e.g. "tickets" → count of my open tickets,
	 * "redirects" → open production link-consistency tasks, "contact" → new contact messages). */
	badge?: "tickets" | "redirects" | "contact";
};

export type NavEntry =
	| {
			type: "section";
			label: string;
	  }
	| NavItem
	| {
			type: "group";
			key: string;
			label: string;
			to: string;
			icon: IconType;
			children: NavItem[];
	  };

/**
 * Single source of truth for the sidebar. Order here IS the display order. This is the full map for
 * a root admin : `Components/Layout.tsx` filters it per user (items whose route the current role may
 * not read are dropped, then empty sections/groups collapse away), and `RouteHeaderSync` derives the
 * page title from it, so adding a route generally means adding exactly one entry here.
 */
export const NAV_ENTRIES: NavEntry[] = [
	{ type: "section", label: "Overview" },
	{ type: "item", to: "/dashboard", label: "Dashboard", icon: FiGrid },
	{ type: "section", label: "Content" },
	{
		type: "group",
		key: "content",
		label: "Content",
		to: "/content",
		icon: FiFileText,
		children: [
			{ type: "item", to: "/pages", label: "Pages", icon: FiFileText }
		]
	},
	{ type: "item", to: "/traduction", label: "Traduction", icon: FiGlobe },
	// The layout / languages / contact-info singletons sit at the bottom of the Content section,
	// below Traduction : they're rarely-touched config, not day-to-day content.
	{ type: "item", to: "/layout", label: "Layout", icon: FiLayout },
	{ type: "item", to: "/languages", label: "Languages", icon: FiBook },
	{ type: "item", to: "/contact-info", label: "Contact Info", icon: FiMail },

	{ type: "section", label: "Engagement" },
	{ type: "item", to: "/tickets", label: "Tickets", icon: FiInbox, badge: "tickets" },
	{ type: "item", to: "/newsletters", label: "Newsletters", icon: FiMail },
	{ type: "item", to: "/contact-messages", label: "Contact Messages", icon: FiBook, badge: "contact" },
	{ type: "item", to: "/recrutement", label: "Recrutement", icon: FiBriefcase },

	{ type: "section", label: "Monitoring" },
	{ type: "item", to: "/macro-data", label: "Données macroéconomiques", icon: FiDatabase },
	{ type: "item", to: "/exchange-rates", label: "Exchange Rates", icon: FiDollarSign },
	{ type: "item", to: "/network-status", label: "Network Status", icon: FiWifi },
	{ type: "item", to: "/metrics", label: "Metrics", icon: FiTrendingUp },
	{ type: "item", to: "/search-metrics", label: "Métriques de recherche", icon: FiSearch },
	{ type: "item", to: "/analytics", label: "Analytics", icon: FiActivity },
	{ type: "item", to: "/audit", label: "Audit", icon: FiShield },
	{ type: "item", to: "/logs", label: "Logs", icon: FiList },
	{ type: "item", to: "/sitemaps", label: "Sitemaps", icon: FiMap },
	{ type: "item", to: "/backup", label: "Backup", icon: FiRefreshCw },

	{ type: "section", label: "Administration" },
	{ type: "item", to: "/external-api", label: "API interne", icon: FiKey },
	{ type: "item", to: "/deployment", label: "Déploiement", icon: FiUploadCloud },
	{ type: "item", to: "/fragments", label: "Fragments", icon: FiBox },
	{ type: "item", to: "/deleted-pages", label: "Archive content", icon: FiRotateCcw },
	{ type: "item", to: "/users", label: "Users", icon: FiUsers },
	{ type: "item", to: "/permissions", label: "Permissions", icon: FiShield },
	{ type: "item", to: "/storage", label: "Stockage", icon: FiCloud },
	{ type: "item", to: "/redirections", label: "Redirections", icon: FiCornerUpRight, badge: "redirects" },
	{ type: "item", to: "/settings", label: "Settings", icon: FiSettings },

	// Structure & reference : the data model + taxonomy/tagging + the site map. Not day-to-day content,
	// so it sits below Administration rather than cluttering the Content area.
	{ type: "section", label: "Structure" },
	{ type: "item", to: "/content-types", label: "Content Types", icon: FiLayers },
	{ type: "item", to: "/taxonomie", label: "Taxonomie", icon: FiGitBranch },
	{ type: "item", to: "/categories", label: "Categories", icon: FiFolder },
	{ type: "item", to: "/search-tags", label: "Search Tags", icon: FiTag },
	{ type: "item", to: "/design-library", label: "Design Library", icon: FiPenTool }
];
